import { useEffect, useState } from 'react';
import { ShieldCheck, Upload, Pencil, X } from 'lucide-react';
import ModalShell from '../ui/ModalShell';
import Dropdown from '../receptura/Dropdown';
import { api, hlavicky, barva as barvaZnalosti } from './znalosti';

/**
 * Modaly záložky Znalosti → Směrnice: nová směrnice, úprava údajů a nová
 * verze. Rychlé akce s koncem (norma §1f) — detail směrnice zůstává vidět
 * ve sloupci, editace se do něj necpe.
 */

// Komu může směrnice patřit. Bez přiřazení ji nevidí nikdo.
export const TYP_CILE = { vsichni: 'Všichni', kariera: 'Kariéra', pozice: 'Pozice', oddeleni: 'Oddělení', zamestnanec: 'Zaměstnanec' };
export const nazevCile = (item) => item?.cele_jmeno ?? (item?.jmeno && item?.prijmeni ? `${item.jmeno} ${item.prijmeni}` : item?.nazev) ?? '?';
export const seznamCile = (ciselniky, typ) => ({
    kariera: ciselniky?.kariery, pozice: ciselniky?.pozice, oddeleni: ciselniky?.oddeleni, zamestnanec: ciselniky?.zamestnanci,
}[typ] || []);

const PRIPONY = '.pdf,.doc,.docx,.jpg,.jpeg,.png,.xlsx,.xls,.csv,.txt';

/** Číselníky pro výběr cíle přiřazení — načtou se jednou, až jsou potřeba. */
let ciselnikyCache = null;
export function nactiCiselnikyCilu() {
    ciselnikyCache ??= Promise.all([
        fetch('/api/zamestnanci').then(r => r.json()).catch(() => []),
        fetch('/api/ciselniky/oddeleni').then(r => r.json()).catch(() => []),
        fetch('/api/ciselniky/pozice').then(r => r.json()).catch(() => []),
        fetch('/api/ciselniky/kariery').then(r => r.json()).catch(() => []),
    ]).then(([zamestnanci, oddeleni, pozice, kariery]) => ({ zamestnanci, oddeleni, pozice, kariery }));
    return ciselnikyCache;
}

/** Nahrání souboru (multipart) — `api()` posílá jen JSON. */
async function posliSoubor(url, fd) {
    const res = await fetch(url, { method: 'POST', headers: hlavicky(false), body: fd });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
        const prvni = data?.errors ? Object.values(data.errors)[0]?.[0] : null;
        throw new Error(prvni || data?.error || data?.message || (res.status === 413 ? 'Soubor je moc velký' : 'Nahrání se nepovedlo'));
    }
    return data;
}

function useStyly(isDark) {
    const cara = isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)';
    const muted = isDark ? 'rgba(255,255,255,0.55)' : 'var(--text-secondary)';
    return {
        cara, muted,
        input: {
            width: '100%', minHeight: 44, borderRadius: 10, padding: '0 12px', outline: 'none', fontSize: 14,
            background: isDark ? 'rgba(0,0,0,0.35)' : 'var(--surface-raised)', border: `1px solid ${cara}`,
            color: 'var(--text-primary)', colorScheme: isDark ? 'dark' : 'light',
        },
        stitek: { display: 'block', fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: muted, margin: '14px 0 6px' },
    };
}

function Paticka({ chyba, ukladam, lze, popis, onClick, akcent }) {
    return (
        <div>
            {chyba && <p style={{ margin: '0 0 8px', fontSize: 13, color: '#EF4444' }}>{chyba}</p>}
            <button type="button" onClick={onClick} disabled={ukladam || !lze}
                style={{ height: 48, width: '100%', borderRadius: 12, fontWeight: 700, fontSize: 14, border: 'none', cursor: 'pointer', background: akcent, color: '#fff', opacity: ukladam || !lze ? 0.5 : 1 }}>
                {ukladam ? 'Ukládám…' : popis}
            </button>
        </div>
    );
}

/** Výběr souboru — nativní input schovaný za tlačítkem s názvem souboru. */
function VyberSouboru({ soubor, onZmena, isDark }) {
    const { cara, muted } = useStyly(isDark);
    return (
        <label className="flex items-center" style={{ gap: 10, minHeight: 44, padding: '0 12px', borderRadius: 10, border: `1px dashed ${cara}`, cursor: 'pointer', color: soubor ? 'var(--text-primary)' : muted, fontSize: 14 }}>
            <Upload size={16} style={{ flexShrink: 0 }} />
            <span className="truncate flex-1">{soubor ? soubor.name : 'Vybrat soubor (PDF, Word, obrázek…)'}</span>
            <input type="file" accept={PRIPONY} onChange={e => onZmena(e.target.files?.[0] || null)} style={{ display: 'none' }} />
        </label>
    );
}

/**
 * Výběr cíle přiřazení: typ + konkrétní položka (u „Všichni" jen typ).
 * Sdílí ho nová směrnice i detail.
 */
export function CilPrirazeni({ ciselniky, isDark, sVsemi = true, onPridat, kompaktni = false }) {
    const akcent = barvaZnalosti(isDark);
    const [form, setForm] = useState({ typ_cile: sVsemi ? 'vsichni' : 'pozice', cil_id: '' });
    const typy = Object.entries(TYP_CILE).filter(([id]) => sVsemi || id !== 'vsichni');
    const lze = form.typ_cile === 'vsichni' || !!form.cil_id;
    const pridat = () => {
        if (!lze) return;
        const polozka = seznamCile(ciselniky, form.typ_cile).find(i => i.id === form.cil_id);
        onPridat({ typ_cile: form.typ_cile, cil_id: form.typ_cile === 'vsichni' ? null : form.cil_id, display_name: form.typ_cile === 'vsichni' ? 'Všichni zaměstnanci' : nazevCile(polozka) });
        setForm(f => ({ ...f, cil_id: '' }));
    };
    return (
        <div className={`flex ${kompaktni ? 'flex-col' : ''}`} style={{ gap: 8 }}>
            <div style={{ minWidth: 140 }}>
                <Dropdown value={form.typ_cile} isDark={isDark} accent={akcent} height={40}
                    options={typy.map(([value, label]) => ({ value, label }))}
                    onChange={v => setForm({ typ_cile: v, cil_id: '' })} />
            </div>
            {form.typ_cile !== 'vsichni' && (
                <div style={{ flex: 1, minWidth: 0 }}>
                    <Dropdown value={form.cil_id} isDark={isDark} accent={akcent} height={40} placeholder={ciselniky ? '— Vyberte —' : 'Načítám…'}
                        options={seznamCile(ciselniky, form.typ_cile).map(item => ({ value: item.id, label: nazevCile(item) }))}
                        onChange={v => setForm(f => ({ ...f, cil_id: Number(v) }))} />
                </div>
            )}
            <button type="button" onClick={pridat} disabled={!lze}
                style={{ height: 40, padding: '0 14px', borderRadius: 10, border: `1px solid ${akcent}`, fontWeight: 700, fontSize: 13, cursor: 'pointer', background: 'transparent', color: akcent, opacity: lze ? 1 : 0.4 }}>
                Přidat
            </button>
        </div>
    );
}

/** Štítek přiřazení s křížkem. */
export function StitekPrirazeni({ p, isDark, onOdebrat }) {
    return (
        <span className="inline-flex items-center" style={{ gap: 6, padding: '4px 6px 4px 10px', borderRadius: 999, fontSize: 12.5, background: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)', color: 'var(--text-primary)' }}>
            {p.typ_cile !== 'vsichni' && <span style={{ opacity: 0.55 }}>{TYP_CILE[p.typ_cile]}</span>}
            {p.display_name}
            {onOdebrat && (
                <button type="button" onClick={onOdebrat} aria-label={`Odebrat ${p.display_name}`}
                    style={{ display: 'inline-grid', placeItems: 'center', width: 22, height: 22, borderRadius: 11, border: 'none', background: 'none', color: 'var(--text-secondary)', cursor: 'pointer' }}>
                    <X size={13} />
                </button>
            )}
        </span>
    );
}

/** Nová směrnice: údaje, soubor a komu patří (povinné — bez přiřazení ji nikdo nevidí). */
export function NovaSmerniceModal({ isDark, kategorie, onClose, onUlozeno }) {
    const akcent = barvaZnalosti(isDark);
    const { input, stitek, muted } = useStyly(isDark);
    const [f, setF] = useState({ nazev: '', popis: '', kategorie_id: kategorie[0]?.id ?? '', vyzaduje_podpis: true, soubor: null });
    const [komu, setKomu] = useState([{ typ_cile: 'vsichni', cil_id: null, display_name: 'Všichni zaměstnanci' }]);
    const [ciselniky, setCiselniky] = useState(null);
    const [chyba, setChyba] = useState(null);
    const [ukladam, setUkladam] = useState(false);
    useEffect(() => { nactiCiselnikyCilu().then(setCiselniky); }, []);
    const zmen = (k, v) => setF(s => ({ ...s, [k]: v }));
    const lze = f.nazev.trim() && f.soubor && f.kategorie_id && komu.length > 0;

    const ulozit = async () => {
        setUkladam(true); setChyba(null);
        try {
            const fd = new FormData();
            fd.append('nazev', f.nazev.trim());
            fd.append('popis', f.popis);
            fd.append('kategorie_id', f.kategorie_id);
            fd.append('vyzaduje_podpis', f.vyzaduje_podpis ? '1' : '0');
            fd.append('soubor', f.soubor);
            const nova = await posliSoubor('/api/bezpecnost/smernice', fd);
            // Přiřazení až po uložení — teprve s ním jdou lidem upozornění
            for (const p of komu) {
                await api(`/api/bezpecnost/smernice/${nova.id}/prirazeni`, { method: 'POST', body: { typ_cile: p.typ_cile, cil_id: p.cil_id } });
            }
            onUlozeno(nova);
        } catch (e) {
            setChyba(e.message);
            setUkladam(false);
        }
    };

    return (
        <ModalShell onClose={onClose} isDark={isDark} nadpis="Nová směrnice" ikona={ShieldCheck} barva={akcent} sirka={520}
            paticka={<Paticka chyba={chyba} ukladam={ukladam} lze={lze} popis="Nahrát směrnici" onClick={ulozit} akcent={akcent} />}>
            <div style={{ padding: '0 2px 8px' }}>
                <label style={{ ...stitek, marginTop: 4 }}>Název</label>
                <input style={input} value={f.nazev} onChange={e => zmen('nazev', e.target.value)} placeholder="Požární ochrana" maxLength={300} />
                <label style={stitek}>Popis</label>
                <textarea style={{ ...input, minHeight: 72, padding: '10px 12px', resize: 'vertical' }} value={f.popis} onChange={e => zmen('popis', e.target.value)} maxLength={2000} placeholder="Volitelné — o čem směrnice je" />
                <label style={stitek}>Kategorie</label>
                <Dropdown value={f.kategorie_id} isDark={isDark} accent={akcent} height={44}
                    options={kategorie.map(k => ({ value: k.id, label: `${k.kod ? `[${k.kod}] ` : ''}${k.nazev}` }))}
                    onChange={v => zmen('kategorie_id', Number(v))} />
                <label style={stitek}>Soubor</label>
                <VyberSouboru soubor={f.soubor} onZmena={s => zmen('soubor', s)} isDark={isDark} />
                <label className="flex items-center" style={{ gap: 10, marginTop: 14, cursor: 'pointer', fontSize: 14, color: 'var(--text-primary)' }}>
                    <input type="checkbox" checked={f.vyzaduje_podpis} onChange={e => zmen('vyzaduje_podpis', e.target.checked)} style={{ width: 18, height: 18, accentColor: akcent }} />
                    Zaměstnanci musí potvrdit, že ji přečetli
                </label>
                <label style={stitek}>Komu patří</label>
                {komu.length > 0 ? (
                    <div className="flex flex-wrap" style={{ gap: 6, marginBottom: 8 }}>
                        {komu.map((p, i) => <StitekPrirazeni key={`${p.typ_cile}:${p.cil_id}`} p={p} isDark={isDark} onOdebrat={() => setKomu(k => k.filter((_, j) => j !== i))} />)}
                    </div>
                ) : <p style={{ margin: '0 0 8px', fontSize: 12.5, color: '#EF4444' }}>Bez přiřazení směrnici nikdo neuvidí.</p>}
                <CilPrirazeni ciselniky={ciselniky} isDark={isDark} kompaktni
                    onPridat={(p) => setKomu(k => {
                        // „Všichni" přebije konkrétní cíle a naopak
                        if (p.typ_cile === 'vsichni') return [p];
                        const bez = k.filter(x => x.typ_cile !== 'vsichni');
                        return bez.some(x => x.typ_cile === p.typ_cile && x.cil_id === p.cil_id) ? bez : [...bez, p];
                    })} />
                <p style={{ margin: '8px 0 0', fontSize: 12, color: muted }}>Upozornění dostanou lidé hned po nahrání.</p>
            </div>
        </ModalShell>
    );
}

/** Úprava údajů směrnice (soubor se mění novou verzí). */
export function UpravitSmerniciModal({ isDark, smernice: s, kategorie, onClose, onUlozeno }) {
    const akcent = barvaZnalosti(isDark);
    const { input, stitek } = useStyly(isDark);
    const [f, setF] = useState({ nazev: s.nazev, popis: s.popis || '', kategorie_id: s.kategorie_id, vyzaduje_podpis: !!s.vyzaduje_podpis });
    const [chyba, setChyba] = useState(null);
    const [ukladam, setUkladam] = useState(false);
    const zmen = (k, v) => setF(x => ({ ...x, [k]: v }));

    const ulozit = async () => {
        setUkladam(true); setChyba(null);
        try {
            await api(`/api/bezpecnost/smernice/${s.id}`, { method: 'PUT', body: { ...f, nazev: f.nazev.trim() } });
            onUlozeno();
        } catch (e) {
            setChyba(e.message);
            setUkladam(false);
        }
    };

    return (
        <ModalShell onClose={onClose} isDark={isDark} nadpis="Upravit směrnici" ikona={Pencil} barva={akcent} sirka={520}
            paticka={<Paticka chyba={chyba} ukladam={ukladam} lze={!!f.nazev.trim()} popis="Uložit" onClick={ulozit} akcent={akcent} />}>
            <div style={{ padding: '0 2px 8px' }}>
                <label style={{ ...stitek, marginTop: 4 }}>Název</label>
                <input style={input} value={f.nazev} onChange={e => zmen('nazev', e.target.value)} maxLength={300} />
                <label style={stitek}>Popis</label>
                <textarea style={{ ...input, minHeight: 72, padding: '10px 12px', resize: 'vertical' }} value={f.popis} onChange={e => zmen('popis', e.target.value)} maxLength={2000} />
                <label style={stitek}>Kategorie</label>
                <Dropdown value={f.kategorie_id} isDark={isDark} accent={akcent} height={44}
                    options={kategorie.map(k => ({ value: k.id, label: `${k.kod ? `[${k.kod}] ` : ''}${k.nazev}` }))}
                    onChange={v => zmen('kategorie_id', Number(v))} />
                <label className="flex items-center" style={{ gap: 10, marginTop: 14, cursor: 'pointer', fontSize: 14, color: 'var(--text-primary)' }}>
                    <input type="checkbox" checked={f.vyzaduje_podpis} onChange={e => zmen('vyzaduje_podpis', e.target.checked)} style={{ width: 18, height: 18, accentColor: akcent }} />
                    Zaměstnanci musí potvrdit, že ji přečetli
                </label>
            </div>
        </ModalShell>
    );
}

/** Nová verze souboru — lidé, kterým směrnice patří, ji potvrzují znovu. */
export function NovaVerzeModal({ isDark, smernice: s, onClose, onUlozeno }) {
    const akcent = barvaZnalosti(isDark);
    const { input, stitek, muted } = useStyly(isDark);
    const [soubor, setSoubor] = useState(null);
    const [poznamka, setPoznamka] = useState('');
    const [chyba, setChyba] = useState(null);
    const [ukladam, setUkladam] = useState(false);
    const cislo = (s.aktualni_verze?.cislo_verze || s.verze_count || 1) + 1;

    const ulozit = async () => {
        setUkladam(true); setChyba(null);
        try {
            const fd = new FormData();
            fd.append('soubor', soubor);
            if (poznamka.trim()) fd.append('poznamka', poznamka.trim());
            await posliSoubor(`/api/bezpecnost/smernice/${s.id}/verze`, fd);
            onUlozeno();
        } catch (e) {
            setChyba(e.message);
            setUkladam(false);
        }
    };

    return (
        <ModalShell onClose={onClose} isDark={isDark} nadpis={`Nová verze · v${cislo}`} ikona={Upload} barva={akcent}
            paticka={<Paticka chyba={chyba} ukladam={ukladam} lze={!!soubor} popis="Nahrát verzi" onClick={ulozit} akcent={akcent} />}>
            <div style={{ padding: '0 2px 8px' }}>
                <p style={{ margin: '4px 0 0', fontSize: 13.5, color: 'var(--text-primary)' }}>{s.nazev}</p>
                <label style={stitek}>Soubor</label>
                <VyberSouboru soubor={soubor} onZmena={setSoubor} isDark={isDark} />
                <label style={stitek}>Co se změnilo</label>
                <input style={input} value={poznamka} onChange={e => setPoznamka(e.target.value)} maxLength={2000} placeholder="Volitelné — uvidí to v historii verzí" />
                {s.stav === 'aktivni' && (
                    <p style={{ margin: '10px 0 0', fontSize: 12, color: muted }}>
                        {s.vyzaduje_podpis ? 'Kdo směrnici má, dostane upozornění a potvrdí ji znovu.' : 'Kdo směrnici má, dostane upozornění.'}
                    </p>
                )}
            </div>
        </ModalShell>
    );
}
